import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  MessageSquare, Send, Loader2, Eye, RotateCcw, AlertCircle, CornerDownLeft, ShieldCheck,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from './ui/sonner';
import { AuditTimeline } from './AuditTimeline';
import ChainProgress from './ChainProgress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Generic request-thread + lifecycle UI shared by Payroll Corrections,
 * Leave Requests, and Cash Advances.
 *
 * Props:
 *  - apiPath: base API path WITHOUT the leading {API} prefix, e.g. "/leave-requests/abc123"
 *             or "/corrections/abc123" or "/cash-advances/abc123"
 *  - requestId: id of the request (used only for data-testid scoping)
 *  - isAdmin: boolean — controls whether to show Return-for-Revision UI
 *  - status: current status — "pending" | "returned_for_revision" | "approved" | "rejected" | …
 *             (Return button only renders when status === 'pending' && isAdmin)
 *  - firstViewedAt: ISO string or null — when set, render the viewed badge
 *  - autoMarkViewed: boolean — if true and isAdmin and !firstViewedAt, POST /viewed on mount
 *  - onLifecycleChange: optional () => void — called after a mutation (return / new comment)
 *                       so the parent can re-fetch the request list
 */
const RequestThread = ({
  apiPath,
  requestId,
  isAdmin = false,
  status = 'pending',
  firstViewedAt = null,
  firstViewedByName = null,
  autoMarkViewed = false,
  chainState = null,
  onLifecycleChange,
}) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const [returnComment, setReturnComment] = useState('');
  const [returnError, setReturnError] = useState('');
  const [returning, setReturning] = useState(false);

  const [viewedAt, setViewedAt] = useState(firstViewedAt);
  const [viewedBy, setViewedBy] = useState(firstViewedByName);
  const hasMarkedRef = useRef(false);

  // Audit log state — derive request "kind" from apiPath prefix
  const auditKind = apiPath?.startsWith('/leave-requests') ? 'leave'
                  : apiPath?.startsWith('/cash-advances') ? 'advance'
                  : 'correction';
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);

  const isReturnable = isAdmin && status === 'pending';

  const fetchAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(
        `${API}/audit/request/${auditKind}/${requestId}`,
        { withCredentials: true },
      );
      setAuditEvents(res.data.events || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audit trail');
    } finally {
      setAuditLoading(false);
    }
  };

  const toggleAudit = () => {
    const next = !auditOpen;
    setAuditOpen(next);
    if (next && auditEvents.length === 0 && !auditLoading) {
      fetchAudit();
    }
  };

  // Fetch comments + (admin only) auto-mark-viewed
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}${apiPath}/comments`, { withCredentials: true });
        if (!cancelled) setComments(res.data);
      } catch {
        if (!cancelled) toast.error('Failed to load thread');
      } finally {
        if (!cancelled) setLoading(false);
      }

      if (autoMarkViewed && isAdmin && !viewedAt && !hasMarkedRef.current) {
        hasMarkedRef.current = true;
        try {
          const res = await axios.post(`${API}${apiPath}/viewed`, {}, { withCredentials: true });
          if (!cancelled) {
            setViewedAt(res.data.first_viewed_at || res.data.firstViewedAt);
            setViewedBy(res.data.first_viewed_by_name || res.data.firstViewedByName);
            onLifecycleChange?.();
          }
        } catch {
          // Non-fatal — view tracking shouldn't block the UI
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPath]);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}${apiPath}/comments`,
        { body: text },
        { withCredentials: true },
      );
      setComments((prev) => [...prev, res.data]);
      setBody('');
      onLifecycleChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleReturn = async () => {
    const text = returnComment.trim();
    if (!text) {
      setReturnError('Please add a comment explaining what you need.');
      return;
    }
    setReturnError('');
    setReturning(true);
    try {
      await axios.post(
        `${API}${apiPath}/return`,
        { comment: text },
        { withCredentials: true },
      );
      toast.success('Sent back for revision');
      setReturnComment('');
      onLifecycleChange?.();
      // Refetch comments because /return also stores a 'return' comment
      try {
        const r = await axios.get(`${API}${apiPath}/comments`, { withCredentials: true });
        setComments(r.data);
      } catch {/* non-fatal */}
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to return');
    } finally {
      setReturning(false);
    }
  };

  return (
    <div
      data-testid={`thread-${requestId}`}
      className="border-t border-slate-200 bg-slate-50/50 px-4 py-3 space-y-3"
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      {/* Viewed banner */}
      {viewedAt && (
        <div
          data-testid={`viewed-badge-${requestId}`}
          className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-semibold uppercase tracking-widest rounded border border-emerald-200"
        >
          <Eye className="w-3 h-3" /> Viewed{viewedBy ? ` · ${viewedBy}` : ''}
        </div>
      )}

      {/* Multi-step chain progress (only renders when chain has >1 step) */}
      <ChainProgress chainState={chainState} requestId={requestId} />

      {/* Comment list */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading thread…
          </div>
        ) : comments.length === 0 ? (
          <div className="text-xs text-slate-500 italic flex items-center gap-1.5">
            <MessageSquare className="w-3 h-3" /> No replies yet.
          </div>
        ) : (
          comments.map((c) => {
            const fromAdmin = c.author_role === 'company_admin' || c.author_role === 'super_admin';
            const isReturnLine = c.kind === 'return';
            return (
              <div
                key={c.id}
                data-testid={`comment-${c.id}`}
                className={`p-2.5 rounded text-sm ${
                  isReturnLine
                    ? 'bg-orange-50 border border-orange-200 text-orange-900'
                    : fromAdmin
                    ? 'bg-blue-50 border border-blue-200 text-slate-800'
                    : 'bg-white border border-slate-200 text-slate-800'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">
                    {isReturnLine && (
                      <RotateCcw className="w-3 h-3 inline mr-1 -mt-0.5" />
                    )}
                    {c.author_name}
                    <span className="ml-1.5 font-normal text-slate-400 normal-case">
                      · {new Date(c.created_at).toLocaleString()}
                    </span>
                  </span>
                  {isReturnLine && (
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-orange-700">
                      Returned
                    </span>
                  )}
                </div>
                <p className="whitespace-pre-wrap leading-snug">{c.body}</p>
              </div>
            );
          })
        )}
      </div>

      {/* Reply form */}
      <form onSubmit={handleSend} className="flex items-start gap-2">
        <textarea
          data-testid={`reply-input-${requestId}`}
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a reply…"
          className="flex-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-[#002FA7] bg-white"
          disabled={sending}
        />
        <button
          data-testid={`send-reply-${requestId}`}
          type="submit"
          disabled={sending || !body.trim()}
          className="bg-[#002FA7] text-white px-3 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] transition-colors disabled:opacity-50 flex items-center gap-1.5"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Send
        </button>
      </form>

      {/* Return-for-revision (admin + pending only) */}
      {isReturnable && (
        <div
          data-testid={`return-block-${requestId}`}
          className="border border-orange-200 rounded-md bg-orange-50/60 p-3 space-y-2"
        >
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-orange-800">
            <CornerDownLeft className="w-3 h-3" /> Return for revision
          </div>
          <textarea
            data-testid={`return-comment-${requestId}`}
            rows={2}
            value={returnComment}
            onChange={(e) => { setReturnComment(e.target.value); if (returnError) setReturnError(''); }}
            placeholder="Tell the requester what additional info you need…"
            className="w-full px-2.5 py-1.5 border border-orange-200 rounded-md text-sm focus:outline-none focus:border-orange-400 bg-white"
            disabled={returning}
          />
          {returnError && (
            <p data-testid={`return-error-${requestId}`} className="text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {returnError}
            </p>
          )}
          <button
            data-testid={`return-${requestId}`}
            type="button"
            onClick={handleReturn}
            disabled={returning}
            className="bg-orange-600 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            {returning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Send back for revision
          </button>
        </div>
      )}

      {/* Audit Trail toggle */}
      <div data-testid={`audit-toggle-block-${requestId}`} className="border-t border-slate-200 pt-2">
        <button
          data-testid={`audit-toggle-${requestId}`}
          type="button"
          onClick={toggleAudit}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-600 hover:text-[#002FA7] transition-colors"
        >
          {auditOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <ShieldCheck className="w-3 h-3 text-[#002FA7]" />
          Audit trail
          {auditEvents.length > 0 && (
            <span className="ml-1 text-[10px] font-normal text-slate-400 normal-case">
              · {auditEvents.length} event{auditEvents.length === 1 ? '' : 's'}
            </span>
          )}
        </button>
        {auditOpen && (
          <div data-testid={`audit-panel-${requestId}`} className="bg-white border border-slate-200 rounded-md mt-2">
            <AuditTimeline events={auditEvents} loading={auditLoading} />
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestThread;
